// Prospecção LinkedIn com o Ravi (A21 SDR/BDR): o agente escreve; você envia pelo seu perfil e marca "Enviei".
import { useState } from 'react';
import { api, fmtDate } from '../api';
import { Badge, Button, Card, Empty, ErrorBox, Field, Flash, inputCls, PageHeader, useAction, useApi } from '../components/ui';

interface Message {
  id: string;
  direction: 'out' | 'in';
  kind: string;
  text: string;
  status: 'RASCUNHO' | 'ENVIADA' | 'RECEBIDA';
  dayOffset: number | null;
  sentAt: string | null;
}
interface Prospect {
  id: string;
  name: string;
  title: string | null;
  company: string;
  vertical: string | null;
  linkedinUrl: string;
  context: string | null;
  stage: string;
  nextActionAt: string | null;
  optedOutAt: string | null;
  messages: Message[];
}
interface List {
  invitesToday: number;
  dailyInviteLimit: number;
  dueToday: number;
  prospects: Prospect[];
}

const STAGE: Record<string, [string, 'slate' | 'blue' | 'amber' | 'green' | 'red' | 'violet']> = {
  NOVO: ['Novo', 'slate'],
  CONVITE_ENVIADO: ['Convite enviado', 'blue'],
  CONECTADO: ['Conectado', 'blue'],
  EM_SEQUENCIA: ['Em sequência', 'violet'],
  RESPONDEU: ['Respondeu', 'amber'],
  REUNIAO: ['Reunião marcada', 'green'],
  SEM_INTERESSE: ['Sem interesse', 'slate'],
  OPT_OUT: ['Não contatar', 'red'],
};
const KIND: Record<string, string> = { convite: 'Convite de conexão', mensagem_1: 'Mensagem 1', mensagem_2: 'Mensagem 2', mensagem_3: 'Mensagem 3', resposta: 'Resposta', recebida: 'Recebida' };

function ProspectCard({ p, onChange }: { p: Prospect; onChange: () => void }) {
  const act = useAction();
  const drafts = p.messages.filter((m) => m.status === 'RASCUNHO');
  const next = drafts[0];
  const [text, setText] = useState(next?.text ?? '');
  const [inbound, setInbound] = useState('');
  const [copied, setCopied] = useState(false);
  const history = p.messages.filter((m) => m.status !== 'RASCUNHO');
  const blocked = !!p.optedOutAt;
  const due = p.nextActionAt && new Date(p.nextActionAt) <= new Date() && !blocked;
  if (next && text === '' && next.text) setText(next.text);

  return (
    <li className={`rounded-lg border bg-white p-4 ${due ? 'border-amber-400' : 'border-slate-200'}`}>
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-slate-900">
            {p.name} <span className="font-normal text-slate-600">· {p.title ?? 'cargo não informado'} · {p.company}</span>
          </p>
          <p className="text-xs text-slate-500">
            {p.vertical ?? 'vertical não informada'}
            {p.nextActionAt && !blocked ? ` · próximo passo: ${fmtDate(p.nextActionAt)}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {due && <Badge tone="amber">para hoje</Badge>}
          <Badge tone={STAGE[p.stage]?.[1] ?? 'slate'}>{STAGE[p.stage]?.[0] ?? p.stage}</Badge>
          <a href={p.linkedinUrl} target="_blank" rel="noopener noreferrer" className="rounded bg-[#0a66c2] px-3 py-1 text-sm font-semibold text-white">
            Abrir perfil ↗
          </a>
        </div>
      </div>
      {p.context && <p className="mt-2 text-sm text-slate-600">Contexto: {p.context}</p>}

      {blocked ? (
        <p className="mt-3 rounded bg-red-50 p-2 text-sm text-red-900">Pediu para não ser contatado: nenhuma mensagem nova (LGPD).</p>
      ) : (
        <>
          {!next && ['NOVO', 'CONECTADO', 'EM_SEQUENCIA'].includes(p.stage) && (
            <Button className="mt-3" disabled={act.busy} onClick={() => void act.run(() => api(`/api/prospects/${p.id}/draft`, { method: 'POST' }), 'O Ravi escreveu o convite e a sequência.').then(onChange)}>
              Ravi, escrever convite e sequência
            </Button>
          )}
          {next && (
            <div className="mt-3 rounded border border-brand-100 bg-brand-50 p-3">
              <p className="text-xs font-semibold uppercase text-brand-900">
                Próximo envio: {KIND[next.kind] ?? next.kind}
                {next.kind === 'convite' ? ` (${text.length}/300)` : ''}
              </p>
              <textarea className={`${inputCls} mt-1`} rows={next.kind === 'convite' ? 3 : 5} value={text} onChange={(e) => setText(e.target.value)} aria-label="Texto da mensagem" />
              <div className="mt-2 flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(text).then(() => setCopied(true))}>
                  {copied ? 'Copiado ✓' : 'Copiar texto'}
                </Button>
                <a href={p.linkedinUrl} target="_blank" rel="noopener noreferrer" className="rounded border border-slate-300 px-3 py-2 text-sm">
                  Abrir no LinkedIn ↗
                </a>
                <Button
                  variant="success"
                  disabled={act.busy || (next.kind === 'convite' && text.length > 300)}
                  onClick={() =>
                    void act
                      .run(() => api(`/api/prospect-messages/${next.id}/sent`, { method: 'POST', body: { text } }), 'Registrado como enviado. O próximo passo foi agendado.')
                      .then(() => {
                        setText('');
                        setCopied(false);
                        onChange();
                      })
                  }
                >
                  Enviei pelo LinkedIn
                </Button>
              </div>
              {drafts.length > 1 && <p className="mt-2 text-xs text-slate-600">Depois: {drafts.slice(1).map((d) => `${KIND[d.kind] ?? d.kind} (dia ${d.dayOffset ?? 0})`).join(' · ')}</p>}
            </div>
          )}
          {p.stage === 'CONVITE_ENVIADO' && (
            <Button className="mt-2" variant="secondary" disabled={act.busy} onClick={() => void act.run(() => api(`/api/prospects/${p.id}/connected`, { method: 'POST' }), 'Conexão registrada.').then(onChange)}>
              Convite aceito
            </Button>
          )}
          <details className="mt-3">
            <summary className="cursor-pointer text-sm font-medium text-brand-700">Recebeu resposta? Cole aqui para o Ravi responder</summary>
            <textarea className={`${inputCls} mt-2`} rows={3} value={inbound} onChange={(e) => setInbound(e.target.value)} aria-label="Mensagem recebida" placeholder="Cole a mensagem que a pessoa enviou no LinkedIn" />
            <Button
              className="mt-2"
              disabled={act.busy || inbound.trim().length < 2}
              onClick={() =>
                void act
                  .run(async () => {
                    const r = await api<{ intent: string; nextStep: string }>(`/api/prospects/${p.id}/inbound`, { method: 'POST', body: { text: inbound } });
                    setInbound('');
                    return r;
                  }, 'O Ravi classificou a mensagem e escreveu a resposta (veja o próximo envio).')
                  .then(onChange)
              }
            >
              Ravi, responder
            </Button>
          </details>
        </>
      )}

      {history.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-slate-600">Histórico ({history.length})</summary>
          <ul className="mt-2 space-y-1">
            {history.map((m) => (
              <li key={m.id} className={`rounded p-2 text-sm ${m.direction === 'in' ? 'bg-amber-50' : 'bg-slate-50'}`}>
                <span className="text-xs text-slate-500">
                  {m.direction === 'in' ? 'Recebida' : KIND[m.kind] ?? m.kind} · {m.sentAt ? fmtDate(m.sentAt) : ''}
                </span>
                <p className="whitespace-pre-line">{m.text}</p>
              </li>
            ))}
          </ul>
        </details>
      )}

      {!blocked && (
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {(['REUNIAO', 'SEM_INTERESSE', 'OPT_OUT'] as const).map((s) => (
            <button key={s} className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-50" disabled={act.busy} onClick={() => void act.run(() => api(`/api/prospects/${p.id}/stage`, { method: 'POST', body: { stage: s } }), `Marcado: ${STAGE[s][0]}.`).then(onChange)}>
              {STAGE[s][0]}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}

export function Prospecting() {
  const list = useApi<List>('/api/prospects');
  const act = useAction();
  const [form, setForm] = useState({ name: '', title: '', company: '', vertical: '', linkedinUrl: '', context: '' });
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Prospecção LinkedIn — Ravi (A21)"
        subtitle="O Ravi escreve convites, sequências e respostas; você revisa, envia pelo seu perfil e clica em Enviei. O LinkedIn não permite envio automático: assim o seu perfil fica protegido."
      />
      {list.data && (
        <div className="flex flex-wrap gap-2 text-sm">
          <Badge tone={list.data.dueToday ? 'amber' : 'slate'}>{list.data.dueToday} com próximo passo para hoje</Badge>
          <Badge tone={list.data.invitesToday >= list.data.dailyInviteLimit ? 'red' : 'blue'}>
            Convites hoje: {list.data.invitesToday}/{list.data.dailyInviteLimit}
          </Badge>
        </div>
      )}

      <Card title="Novo prospect">
        <ErrorBox error={act.error} />
        <Flash message={act.message} />
        <div className="grid gap-2 md:grid-cols-2">
          <Field label="Nome" required>
            <input className={inputCls} value={form.name} onChange={set('name')} />
          </Field>
          <Field label="Cargo">
            <input className={inputCls} value={form.title} onChange={set('title')} placeholder="Ex.: CIO, Gerente de Infraestrutura" />
          </Field>
          <Field label="Empresa" required>
            <input className={inputCls} value={form.company} onChange={set('company')} />
          </Field>
          <Field label="Vertical">
            <input className={inputCls} value={form.vertical} onChange={set('vertical')} placeholder="Ex.: Saúde, Logística, Indústria" />
          </Field>
          <Field label="Link do perfil no LinkedIn" required>
            <input className={inputCls} value={form.linkedinUrl} onChange={set('linkedinUrl')} placeholder="https://www.linkedin.com/in/..." />
          </Field>
          <Field label="Contexto para personalizar" hint="Post recente, notícia da empresa, evento em comum. Sem dados sensíveis.">
            <input className={inputCls} value={form.context} onChange={set('context')} />
          </Field>
        </div>
        <Button
          className="mt-3"
          disabled={act.busy || form.name.length < 2 || form.company.length < 2 || form.linkedinUrl.length < 10}
          onClick={() =>
            void act
              .run(
                () =>
                  api('/api/prospects', {
                    method: 'POST',
                    body: Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim())),
                  }),
                'Prospect adicionado. Peça ao Ravi o convite e a sequência.',
              )
              .then(() => {
                setForm({ name: '', title: '', company: '', vertical: '', linkedinUrl: '', context: '' });
                void list.reload();
              })
          }
        >
          Adicionar prospect
        </Button>
      </Card>

      <ErrorBox error={list.error} />
      {list.data?.prospects.length ? (
        <ul className="space-y-3">
          {list.data.prospects.map((p) => (
            <ProspectCard key={`${p.id}-${p.messages.length}-${p.stage}`} p={p} onChange={() => void list.reload()} />
          ))}
        </ul>
      ) : (
        !list.loading && <Empty>Nenhum prospect ainda. Adicione pelo formulário acima.</Empty>
      )}
    </div>
  );
}
